import { relewiseFallback } from '@/shared/constants/constants';
import {
  getActiveFallback,
  installRelewiseFetchMonitor,
  isFallbackEnabled,
  isOwnDomainReachable,
  isRelewiseAppLoaded,
  type RelewiseFailure,
  startFallbackCooldown,
  whenRelewiseChecksSettled
} from '@/shared/instances/relewise-fallback';
import { swapHeaderToNative } from '@/shared/instances/relewise-native-header';
import { redirectToNativeResults } from '@/shared/instances/relewise-native-results';
import { trackSearchFallback } from '@/shared/tracking/search-fallback';
import { isSearchPage } from '@/shared/utils/search-urls';

const confirmSearchFailure = async () => {
  if (!isFallbackEnabled()) {
    return;
  }

  await whenRelewiseChecksSettled(relewiseFallback.RESULTS_ENDPOINT);

  if (await isOwnDomainReachable()) {
    trackSearchFallback({
      reason: 'search_failed',
      surface: 'results_page',
      durationMs: null,
      cooldown: false
    });
    redirectToNativeResults('search_failed');
  }
};

const handleRelewiseFailure = (failure: RelewiseFailure) => {
  const surface =
    failure.endpoint === relewiseFallback.PREDICTIVE_ENDPOINT
      ? 'predictive'
      : failure.endpoint === relewiseFallback.RESULTS_ENDPOINT
        ? 'results_page'
        : null;
  if (!surface) {
    return;
  }

  trackSearchFallback({
    reason: failure.reason,
    surface,
    durationMs: failure.durationMs,
    cooldown: failure.cooldown
  });

  if (surface === 'predictive') {
    swapHeaderToNative(failure.reason);
  } else {
    redirectToNativeResults(failure.reason);
  }
};

const checkRelewiseAppLoaded = async () => {
  if (isRelewiseAppLoaded() || !(await isOwnDomainReachable())) {
    return;
  }

  startFallbackCooldown('script_not_loaded');
  trackSearchFallback({
    reason: 'script_not_loaded',
    surface: isSearchPage() ? 'results_page' : 'predictive',
    durationMs: null,
    cooldown: true
  });
  swapHeaderToNative('script_not_loaded');
  redirectToNativeResults('script_not_loaded');
};

export const handleSearchResultNull = (): void => {
  void confirmSearchFailure();
};

export const initRelewiseFallback = (): void => {
  if (!isFallbackEnabled()) {
    return;
  }

  installRelewiseFetchMonitor(handleRelewiseFailure);

  const activeFallback = getActiveFallback();
  if (activeFallback) {
    swapHeaderToNative(activeFallback.reason);
    redirectToNativeResults(activeFallback.reason);
    return;
  }

  if (document.readyState === 'complete') {
    void checkRelewiseAppLoaded();
  } else {
    window.addEventListener('load', () => void checkRelewiseAppLoaded(), {
      once: true
    });
  }
};
